import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { IAvailableForecastScenario } from '@app/shared/interfaces/iavailable-forecast-scenario';
import { IWellCapital } from '@app/shared/interfaces/iwell-capital';
import { IWellForecastValidationResult } from '@app/shared/interfaces/iwell-forecast-validation-result';
import { CreateCapitalForecastsArgs } from '@app/shared/models/dtos/create-capital-forecasts-args';
import moment from 'moment';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ForecastScenario } from '../forecast-scenarios/forecastScenariosGridOptions';

@Injectable({
  providedIn: 'root'
})
export class ForecastService {
  private http = inject(HttpClient);
  private errorService = inject(ErrorService);
  public environment = inject(PlexEnvironment);

  private baseUrl: string;

  constructor() {
    this.baseUrl = this.environment.apiUrl;
  }

  getAvailableForecastScenarios(): Observable<IAvailableForecastScenario[]> {
    let ob = this.http.get<IAvailableForecastScenario[]>(`${this.baseUrl}capitalforecast/GetAvailableForecastScenarios`);

    return ob.pipe(
      map(results => {
        this.actualizeDates(results, ['CreatedDate', 'PublishedDate']);

        return results;
      }),
      catchError((e) => {
        return this.errorService.handleHTTPError(e);
      })
    );
  }

  getHistoricalForecastScenarios(scenarioId: number): Observable<IAvailableForecastScenario[]> {
    let ob = this.http.get<IAvailableForecastScenario[]>(`${this.baseUrl}capitalforecast/GetHistoricalForecastByScenario?scenarioId=${scenarioId}`);

    return ob.pipe(
      map(results => {
        this.actualizeDates(results, ['LastForecastRunDate']);
        return results;
      }),
      catchError((e) => {
        return this.errorService.handleHTTPError(e);
      })
    );
  }

  updateForecastDetails(data: ForecastScenario[]): Observable<any> {
    const queryString = `${this.baseUrl}capitalforecast/upsertCapitalForecast`;
    return this.http.post(queryString, data).pipe(
      map(results => {
        return results;
      }),
      catchError((e) => {
        return this.errorService.handleHTTPError(e);
      })
    );
  }

  getCapitalForecastsByWell(scenarioId: number): Observable<IWellCapital[]> {
    let ob = this.http.get<IWellCapital[]>(`${this.baseUrl}capitalforecast/GetCapitalWellForecasts?scenarioId=${scenarioId}`);

    return ob.pipe(
      map(results => {
        this.actualizeDates(results, ['SpudDate', 'RigReleaseDate', 'FracStart', 'FracEnd', 'PopDate']);

        return results;
      }),
      catchError((e) => {
        return this.errorService.handleHTTPError(e);
      })
    );
  }

  getHistoricalCapitalForecastsByWell(scenarioId: number, parentBuildId: string): Observable<IWellCapital[]> {
    let ob = this.http.get<IWellCapital[]>(`${this.baseUrl}capitalforecast/GetHistoricalCapitalWellForecasts?scenarioId=${scenarioId}&parentBuildId=${parentBuildId}`);

    return ob.pipe(
      map(results => {
        this.actualizeDates(results, ['SpudDate', 'RigReleaseDate', 'FracStart', 'FracEnd', 'PopDate']);

        return results;
      }),
      catchError((e) => {
        return this.errorService.handleHTTPError(e);
      })
    );
  }

  getWellValidationErrors(scenarioId: number, ssn: string, parentBuildId: string): Promise<IWellForecastValidationResult[]> {
    let ob = this.http.get<IWellForecastValidationResult[]>(`${this.baseUrl}capitalforecast/GetWellForecastValidationErrors?scenarioId=${scenarioId}&ssn=${ssn}&parentBuildId=${parentBuildId}`);

    return ob.pipe(
      catchError((e) => {
        return this.errorService.handleHTTPError(e);
      })
    ).toPromise();
  }

  startCapitalForecast(scenarioId: number, effectiveDate: Date, comments: string) {
    let body: CreateCapitalForecastsArgs = {
      IsDetailedForecast: true,
      ScenarioId: scenarioId,
      EffectiveDate: effectiveDate,
      Comments: comments
    };

    let ob = this.http.post(`${this.baseUrl}capitalforecast/StartCapitalForecast`, body);

    return ob.pipe(
      catchError((e) => {
        return this.errorService.handleHTTPError(e);
      })
    );
  }

  private actualizeDates(data: any[], fields: Array<string>) {
    data.forEach(x => {
      fields.forEach(field => {
        x[field] = x[field] != null ? moment(x[field]).startOf('second').toDate() : null;
      });
    })
  }
}